import { createRoot } from "react-dom/client";
import { QueryClientProvider } from "@tanstack/react-query";
import { ClerkProvider } from "@clerk/clerk-react";
import * as Sentry from "@sentry/react";
import App from "./App.tsx";
import { ErrorBoundary } from "./components/routing/ErrorBoundary";
import { queryClient } from "@/shared/lib/queryClient";
import { restorePersistedCache, setupCachePersistence } from "@/shared/lib/queryPersistence";
import { PerformanceMonitor } from "@/shared/lib/performance";
import { startMockServer } from "./mocks";
import { initSentry } from "./config/sentry";
import { initAnalytics } from "./services/analytics";
import { config } from "./config/environment";
import "@/shared/i18n/config";
import "./index.css";

const clerkPubKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

// FIX-CROP-ERRLOG (E1): a root error boundary OUTSIDE every provider (Clerk included), so a throw anywhere shows
// "Something went wrong — Reload" and sends one report instead of a blank page. Dev-only boot tests: never on main.
//   ?__boot_test=throw  → a component throws during render (the boundary's screen + a 'render' report)
//   ?__boot_test=blank  → the app is not rendered at all (the inline script's 'blank' report after 8 s)
const BOOT_TEST = __APP_VERSION__.branch !== 'main' ? new URLSearchParams(window.location.search).get('__boot_test') : null;
const BootThrow = () => { throw new Error('boot-test: render throw'); };

// Initialize error tracking
initSentry();

// Initialize analytics
initAnalytics();

// Render the app
const renderApp = () => {
  console.log('📱 Rendering React app...');
  createRoot(document.getElementById("root")!).render(
    <ErrorBoundary>
      {BOOT_TEST === 'throw' ? <BootThrow /> : null}
      <ClerkProvider publishableKey={clerkPubKey}>
        <QueryClientProvider client={queryClient}>
          <App />
          <PerformanceMonitor enabled={config.enablePerformanceMonitoring} />
        </QueryClientProvider>
      </ClerkProvider>
    </ErrorBoundary>
  );
  console.log('✅ React app rendered');
};

// Initialize app with cache restoration
const initializeApp = async () => {
  // Restore persisted query cache from IndexedDB
  console.log('💾 Restoring query cache...');
  await restorePersistedCache();
  
  // Setup automatic cache persistence
  setupCachePersistence();
  
  // Render the app
  if (BOOT_TEST === 'blank') { console.warn('[boot-test] blank: the app is deliberately not rendered'); return; }
  renderApp();
  
  // In development, start MSW in the background
  if (import.meta.env.DEV) {
    console.log('🎭 Starting Mock Service Worker in background...');
    startMockServer()
      .then(() => {
        console.log('✅ Mock Service Worker started successfully');
      })
      .catch((err) => {
        console.error('❌ Failed to start MSW:', err);
        console.warn('⚠️ App will work but API calls may fail');
      });
  }
};

// Start initialization
console.log('🚀 Starting app...');
initializeApp();
